<script setup lang="ts">
import { ref, watch } from 'vue'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const agreed = ref(props.modelValue)
const error = ref('')

watch(agreed, (val) => emit('update:modelValue', val))

const DISCLOSURES = [
  'I understand that most investments in early-stage ventures involve high risk and may result in a total loss.',
  'I acknowledge that Venture360 does not independently verify company data, and I am responsible for conducting my own due diligence.',
  'I agree to hold Venture360 harmless from any losses or damages that may arise from my use of the platform.',
  'If I choose to invest, I will comply with applicable securities regulations and consult legal or financial advisors as needed.',
  'I understand that providing false information regarding my investor status may lead to permanent suspension from Venture360 and potential legal consequences.',
  'I acknowledge that past performance of any investment is not indicative of future results, and that projections or forward-looking statements are inherently uncertain.',
  'I understand that private investments are illiquid, and I may not be able to sell or transfer my holdings for an extended period of time.',
  'I confirm that I am investing on my own behalf (or on behalf of an entity I am authorized to represent) and that the funds I use are from lawful sources.',
]

const toggleAgree = () => {
  agreed.value = !agreed.value
  if (agreed.value) error.value = ''
}

const isValid = async () => {
  if (!agreed.value) {
    error.value = 'You must agree to the disclosures to continue.'
    return false
  }
  error.value = ''
  return true
}

defineExpose({ isValid })
</script>

<template>
  <div>
    <div class="rounded-[10px] border border-[#f0f0f0] bg-white p-6">
      <div v-for="(text, index) in DISCLOSURES" :key="index" class="mb-5 flex items-start gap-3 last:mb-0">
        <div
          class="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[rgba(0,145,146,0.12)] bg-[linear-gradient(135deg,rgba(0,145,146,0.08),rgba(0,132,180,0.08))] text-[11px] font-semibold text-[#009192]"
        >
          {{ index + 1 }}
        </div>
        <p class="m-0 text-[13px] leading-[1.6] text-[#4a4a4a]">{{ text }}</p>
      </div>

      <button
        type="button"
        class="mt-7 flex w-full items-center gap-3 rounded-md border-none px-6 py-5 text-left shadow-sm ring-1 transition-shadow active:translate-y-px active:shadow-sm"
        :class="
          agreed
            ? 'bg-[#009192]/[0.08] text-gray-900 ring-[#009192] hover:shadow-md'
            : 'bg-white text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30'
        "
        @click="toggleAgree"
      >
        <div
          class="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-[1.5px] transition-all duration-150"
          :class="agreed ? 'border-[#009192] bg-[#009192]' : 'border-[#d1d5db] bg-white'"
        >
          <svg
            v-if="agreed"
            width="11"
            height="11"
            fill="none"
            stroke="#fff"
            stroke-width="2.5"
            viewBox="0 0 24 24"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <span class="text-sm font-semibold leading-snug">
          I acknowledge and accept the risk disclosures outlined above.
        </span>
      </button>
    </div>

    <p v-if="error" class="mt-2 text-sm text-red-500">{{ error }}</p>
  </div>
</template>
